import { useQuery } from "@tanstack/react-query";

import { Team } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { isAutoptoTrialing } from "~/components/autopto/isAutoptoTrial";
import { useTeam } from "~/contexts/TeamProvider";
import { tryCatch } from "~/helpers/tryCatch";

const PROBES = {
  memberships: () => apiClient.memberships.membershipsList({ "page[size]": 1 }),
  accessTokens: () =>
    apiClient.accessTokens.accessTokensList({ "page[size]": 1 }),
  orders: () =>
    apiClient.billing.ordersList({ type: "active", "page[size]": 1 }),
  invoices: () => apiClient.billing.invoicesList({ "page[size]": 1 }),
};

export type ProbedEndpoint = keyof typeof PROBES;

const ACCESS_STALE_TIME_MS = 5 * 60 * 1000;

// Where the backend mostly refuses, before it answers. A card opens the
// sections during an Autopto trial, but not the invoices: they wait for
// the trial's end.
const isExpectedClosed = (endpoint: ProbedEndpoint, team: Team) =>
  endpoint === "invoices"
    ? isAutoptoTrialing(team)
    : team.autopto_card_required;

/**
 * Whether the backend lets the current user into a section, asked by a
 * minimal request to its endpoint: 403 means no. The rules stay in the
 * backend abilities (e.g. an Autopto agency without a card has no Team
 * members, API keys or Services), the frontend only reflects them.
 *
 * Most teams may open these sections, so until the answer arrives (or if
 * none does) they count as allowed (no menu items popping in on every
 * load), and a 403 hides the section afterwards. Where the backend mostly
 * refuses (an Autopto agency without a card; the invoices of any Autopto
 * trial) they stay unknown (undefined) instead, so callers don't flash
 * links the team can't open. Until the team loads they're unknown too.
 */
export function useEndpointAccess(
  endpoint: ProbedEndpoint,
  enabled = true,
): boolean | undefined {
  const { teamId, team } = useTeam();

  // What the section counts as until the backend answers, and when it
  // never does (a network failure after the retries).
  const assumedAccess =
    team && !isExpectedClosed(endpoint, team) ? true : undefined;

  const { data } = useQuery({
    // Re-probe when the team's billing state changes (e.g. a card added
    // during an Autopto trial opens the sections).
    queryKey: [
      "endpoint-access",
      endpoint,
      teamId,
      team?.subscription?.status,
      team?.autopto_card_required,
    ],
    // The key depends on the team: wait for it, or every probe goes twice.
    enabled: enabled && !!team,
    staleTime: ACCESS_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch<unknown>(PROBES[endpoint]());

      if (!result.error) return true;
      if (result.error.status === 403) return false;
      // Any other answer isn't a denial: don't hide the section over it.
      if (result.error.status) return true;

      // No answer (network): let the query retry.
      throw result.error;
    },
  });

  return data ?? assumedAccess;
}
